/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable react/prop-types */
import currency from "currency.js";
import { floorToStepString } from "../../../../utils/format";
import { RFQ_RATIO_STEP } from "./helpers";
import { TFunction } from "i18next";
import moment from "moment";
import { UseFormRegister, UseFormSetValue } from "react-hook-form";
import {
  InstrumentTypeResponse,
  SideResponse,
} from "../../../../codegen-api";
import Dropdown, {
  IDropdownItemsItem,
} from "../../../../components/shared/Dropdown";
import { Input } from "../../../../components/shared/Input";
import RowActionButton from "../../../../components/shared/RowActionButton";
import {
  Align,
  ButtonGroup,
  GroupedCells,
} from "../../../../components/shared/Table/style";
import TickerLogo from "../../../../components/shared/TickerLogo";
import { COLORS } from "../../../../constants/design/colors";
import { SPACING } from "../../../../constants/design/spacing";
import { nanosToSeconds } from "../../../../utils/date";
import { AssetTag, EditorRow, EmptyContent, dropdownToggleStyles } from "./style";
import { IFormStrategy, IFormValues } from "./types";

interface IStrategyRowProps {
  row: any;
  prepareInstrumentTypeDropdown: (index: number) => IDropdownItemsItem[];
  prepareAssetDropdown: (index: number) => IDropdownItemsItem[];
  prepareSidesDropdown: (index: number) => IDropdownItemsItem[];
  prepareOptionTypeDropdown: (index: number) => IDropdownItemsItem[];
  prepareExpiryDropdown: (index: number) => IDropdownItemsItem[];
  prepareStrikeDropdown: (index: number) => IDropdownItemsItem[];
  strategies: IFormStrategy[];
  register: UseFormRegister<IFormValues>;
  setValue: UseFormSetValue<IFormValues>;
  t: TFunction<"app", "pages.RFQPage.CreateRFQ">;
  onDeleteLeg: (index: number) => void;
  rows: any[];
}

export function StrategyRow({
  row,
  prepareInstrumentTypeDropdown,
  prepareAssetDropdown,
  prepareSidesDropdown,
  prepareOptionTypeDropdown,
  prepareExpiryDropdown,
  prepareStrikeDropdown,
  strategies,
  register,
  setValue,
  t,
  onDeleteLeg,
  rows,
}: IStrategyRowProps) {
  const { key, ...rowProps } = row.getRowProps();

  if (!row.original.isEdit) {
    return (
      <tr key={key} {...rowProps}>
        {row.cells.map((cell: any) => {
          const { key, ...tdProps } = cell.getCellProps();
          return (
            <td key={key} {...tdProps}>
              {cell.render("Cell")}
            </td>
          );
        })}
      </tr>
    );
  }

  const instrumentTypeOptions =
    prepareInstrumentTypeDropdown(row.index);
  const assetOptions = prepareAssetDropdown(row.index);
  const sideOptions = prepareSidesDropdown(row.index);
  const optionTypeOptions = prepareOptionTypeDropdown(
    row.index
  );
  const expiryOptions = prepareExpiryDropdown(row.index);
  const strikeOptions = prepareStrikeDropdown(row.index);
  const strategy = strategies[row.index];

  return (
    <EditorRow
      key={String(row.id)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <td>
        <Dropdown
          showArrow
          toggleStyles={dropdownToggleStyles}
          width={"140px"}
          title={
            strategy.asset ? (
              <AssetTag>
                <TickerLogo asset={strategy.asset} size={24} />
                <div>{strategy.asset}</div>
              </AssetTag>
            ) : (
              t("select_asset")
            )
          }
          items={assetOptions}
          {...register(`strategies.${row.index}.asset`, {
            required: true,
          })}
        />
      </td>
      <td>
        <Dropdown
          showArrow
          toggleStyles={dropdownToggleStyles}
          width={"180px"}
          title={t(
            (
              strategy.instrument_type ??
              "select_instrument_type"
            ).toLowerCase()
          )}
          items={instrumentTypeOptions}
          {...register(
            `strategies.${row.index}.instrument_type`,
            {
              required: true,
            }
          )}
        />
      </td>
      <td>
        <Dropdown
          showArrow
          toggleStyles={dropdownToggleStyles}
          width={"120px"}
          title={
            strategy.side ? (
              <div
                style={{
                  display: "flex",
                  color:
                    strategy.side === SideResponse.Buy
                      ? COLORS.up.one
                      : COLORS.down.one,
                }}
              >
                {t(strategy.side)}
              </div>
            ) : (
              t("select_side")
            )
          }
          items={sideOptions}
          {...register(`strategies.${row.index}.side`, {
            required: true,
          })}
        />
      </td>
      <td>
        <div style={{ width: 160 }}>
          {strategy.instrument_type ===
          InstrumentTypeResponse.Option ? (
            <Dropdown
              showArrow
              toggleStyles={dropdownToggleStyles}
              width={"100%"}
              title={t(
                strategy.option_type ?? "select_option_type"
              )}
              items={optionTypeOptions}
              {...register(
                `strategies.${row.index}.option_type`,
                {
                  required:
                    strategy.instrument_type ===
                    InstrumentTypeResponse.Option,
                }
              )}
            />
          ) : (
            <EmptyContent>-</EmptyContent>
          )}
        </div>
      </td>
      <td>
        <div style={{ width: 180 }}>
          {strategy.asset &&
          strategy.instrument_type &&
          strategy.instrument_type ===
            InstrumentTypeResponse.Option ? (
            <Dropdown
              showArrow
              toggleStyles={dropdownToggleStyles}
              width={"100%"}
              title={
                strategy.expiry
                  ? moment
                      .unix(nanosToSeconds(strategy.expiry))
                      .format("DD MMM YY")
                  : t("select_expiry")
              }
              items={expiryOptions}
              {...register(
                `strategies.${row.index}.expiry`,
                {
                  required:
                    strategy.asset &&
                    strategy.instrument_type &&
                    strategy.instrument_type ===
                      InstrumentTypeResponse.Option,
                }
              )}
            />
          ) : (
            <EmptyContent>-</EmptyContent>
          )}
        </div>
      </td>
      <td>
        <div style={{ width: 120 }}>
          {strategy.asset &&
          strategy.instrument_type &&
          strategy.instrument_type ===
            InstrumentTypeResponse.Option &&
          strategy.expiry ? (
            <Dropdown
              showArrow
              width={"100%"}
              toggleStyles={dropdownToggleStyles}
              title={
                strategy.strike
                  ? currency(strategy.strike).format()
                  : t("select_strike")
              }
              items={strikeOptions}
              {...register(
                `strategies.${row.index}.strike`,
                {
                  required:
                    strategy.asset &&
                    strategy.instrument_type &&
                    strategy.instrument_type ===
                      InstrumentTypeResponse.Option &&
                    strategy.expiry,
                }
              )}
            />
          ) : (
            <EmptyContent>-</EmptyContent>
          )}
        </div>
      </td>
      <td>
        <Input
          placeholder="0"
          inputMode="decimal"
          {...register(`strategies.${row.index}.ratio`, {
            onBlur: (e) => {
              // Floored onto the 0.25 grid: `roundToStepSize` rounds half-up,
              // so 0.13 became 0.25 — a bigger leg than was typed — and
              // `.toString()` could write exponent text.
              setValue(
                `strategies.${row.index}.ratio`,
                floorToStepString(e.target.value, RFQ_RATIO_STEP, 2)
              );
            },
            required: true,
            validate: {
              moreThanZero: (v) => v && parseFloat(v) > 0,
            },
          })}
        />
      </td>
      <td>
        <Align align="right">
          <GroupedCells>
            <ButtonGroup>
              {rows.length === 1 ? null : (
                <RowActionButton
                  variant={"close"}
                  onClick={() => onDeleteLeg(row.index)}
                  style={{
                    marginRight: `${SPACING.two}px`,
                  }}
                />
              )}
            </ButtonGroup>
          </GroupedCells>
        </Align>
      </td>
    </EditorRow>
  );
}
